import React from "react";
import {
	PaginationBtn,
	PaginationBtns,
	PaginationInfo,
	PaginationWrap,
	StyledTBody,
	StyledTable,
	TableScrollBox,
} from "../styles";
import TableHead from "./TableHead";
import TableRow from "./TableRow";
import EmptyState from "./EmptyState";
import type { GlossaryTerm, PaginationState, SortState, StationsTextEntry } from "../types";

interface Props {
	entries: StationsTextEntry[];
	totalCount: number;
	sort: SortState;
	pagination: PaginationState;
	selection: Set<string>;
	activeKey: string | null;
	glossary: GlossaryTerm[];
	onSort: (column: SortState["column"]) => void;
	onSelectAll: () => void;
	onSelectNone: () => void;
	onToggleSelect: (key: string) => void;
	onRowClick: (key: string) => void;
	onInlineEdit: (key: string, value: string) => void;
	langs: string[];
	onInlineEditTranslation: (key: string, lang: string, value: string) => void;
	onAddTag: (key: string, tag: string) => void;
	onRemoveTag: (key: string, tag: string) => void;
	onFilterByTag: (tag: string) => void;
	onPaginate: (patch: Partial<PaginationState>) => void;
}

export default function RegistryTable({
	entries,
	totalCount,
	sort,
	pagination,
	selection,
	activeKey,
	glossary,
	onSort,
	onSelectAll,
	onSelectNone,
	onToggleSelect,
	onRowClick,
	onInlineEdit,
	langs,
	onInlineEditTranslation,
	onAddTag,
	onRemoveTag,
	onFilterByTag,
	onPaginate,
}: Props) {
	const { page, pageSize } = pagination;
	const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
	const start = totalCount === 0 ? 0 : page * pageSize + 1;
	const end = Math.min(totalCount, (page + 1) * pageSize);

	const allSelected = entries.length > 0 && entries.every((e) => selection.has(e.key));

	if (totalCount === 0) {
		return <EmptyState />;
	}

	return (
		<>
			<TableScrollBox>
				<StyledTable>
					<TableHead
						langs={langs}
						sort={sort}
						onSort={onSort}
						allSelected={allSelected}
						onSelectAll={onSelectAll}
						onSelectNone={onSelectNone}
					/>
					<StyledTBody>
						{entries.map((entry) => (
							<TableRow
								key={entry.key}
								entry={entry}
								selected={selection.has(entry.key)}
								active={activeKey === entry.key}
								glossary={glossary}
								onToggleSelect={() => onToggleSelect(entry.key)}
								onRowClick={() => onRowClick(entry.key)}
								onInlineEdit={(value) => onInlineEdit(entry.key, value)}
								langs={langs}
								onInlineEditTranslation={(lang, value) =>
									onInlineEditTranslation(entry.key, lang, value)
								}
								onAddTag={(tag) => onAddTag(entry.key, tag)}
								onRemoveTag={(tag) => onRemoveTag(entry.key, tag)}
								onFilterByTag={onFilterByTag}
							/>
						))}
					</StyledTBody>
				</StyledTable>
			</TableScrollBox>

			<PaginationWrap>
				<PaginationInfo>
					Showing {start}–{end} of {totalCount}
				</PaginationInfo>
				<PaginationBtns>
					<PaginationBtn
						disabled={page === 0}
						onClick={() => onPaginate({ page: 0 })}>
						«
					</PaginationBtn>
					<PaginationBtn
						disabled={page === 0}
						onClick={() => onPaginate({ page: page - 1 })}>
						‹
					</PaginationBtn>
					<PaginationBtn active>
						{page + 1} / {totalPages}
					</PaginationBtn>
					<PaginationBtn
						disabled={page >= totalPages - 1}
						onClick={() => onPaginate({ page: page + 1 })}>
						›
					</PaginationBtn>
					<PaginationBtn
						disabled={page >= totalPages - 1}
						onClick={() => onPaginate({ page: totalPages - 1 })}>
						»
					</PaginationBtn>
				</PaginationBtns>
			</PaginationWrap>
		</>
	);
}
